import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  isDevMode,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Subject, takeUntil, tap } from "rxjs";
import { EntityFacadeService } from "../entity-store/entity-facade.service";
import { StoreFacadeService } from "../store/store-facade.service";
import { PaginationFacadeService } from "../pagination/pagination-facade.service";

export const REDUX_DEVTOOL = new InjectionToken<boolean>(
  "store-redux-devtool",
  {
    providedIn: "root",
    factory: () => false,
  },
);

@Injectable()
export class DevtoolService {
  static facades: WritableSignal<
    (
      | EntityFacadeService<any, any>
      | StoreFacadeService<any>
      | PaginationFacadeService<any, any>
    )[]
  > = signal([]);
  static fullState: Signal<any>;
  static injector: Injector;

  static addFacade(
    facade: StoreFacadeService<any> | PaginationFacadeService<any, any>,
  ) {
    facade.updater$.subscribe((action) => {
      window["devTools"].send(
        `${action.name} (${action.type})`,
        this.fullState(),
      );
      if (action.canceler$) {
        toObservable(this.fullState, { injector: DevtoolService.injector })
          .pipe(takeUntil(action.canceler$))
          .subscribe((state) => {
            window["devTools"].send(`${action.name} (processing)`, state);
          });
      }
    }),
      DevtoolService.facades.update((facades) => {
        return [...facades, facade];
      });
  }

  constructor() {
    DevtoolService.injector = inject(Injector);
    if (!window["devTools"]) {
      window["devTools"] = window["__REDUX_DEVTOOLS_EXTENSION__"].connect();
      DevtoolService.fullState = computed(() => {
        const fullState = {};
        for (let f of DevtoolService.facades()) {
          const facade = f as any;
          const state = { state: {} };
          if (facade.elements) {
            state["entities"] = facade.elements().map((e) => {
              const entity = { ...e() };
              if (facade.getMetadata) {
                entity.metadata = facade.getMetadata(entity.id)();
              }
              return entity;
            });
          }
          if (facade.getState) {
            Object.entries(facade.getState()).forEach(
              ([key, value]) =>
                (state["state"][key] = (value as Signal<any>)()),
            );
          }
          fullState[facade.constructor.name] ??= [];
          fullState[facade.constructor.name].push(state);
        }
        return fullState;
      });
      window["devTools"].init(DevtoolService.fullState());
    }
  }
}

function isDevtoolEnabled() {
  const devtool = DevtoolService.injector?.get(REDUX_DEVTOOL);
  return isDevMode() && devtool;
}

function ReduxTrackAsync(
  target: any,
  propertyName: string,
  descriptor: PropertyDescriptor,
) {
  const originalMethod = descriptor.value;
  descriptor.value = async function (...args: any[]) {
    if (!isDevtoolEnabled()) {
      return originalMethod.apply(this, args);
    }

    const entity = this.constructor.name.slice(1, -13);
    const canceler$ = new Subject<void>();
    this.updater$.next({
      name: `[${entity}] ${originalMethod.name}`,
      type: "start",
      canceler$,
    });
    const result = await originalMethod.apply(this, args);
    this.updater$.next({
      name: `[${entity}] ${originalMethod.name}`,
      type: "end",
    });
    canceler$.next();
    return result;
  };
  return descriptor;
}

function ReduxTrackSync(
  target: any,
  propertyName: string,
  descriptor: PropertyDescriptor,
) {
  const originalMethod = descriptor.value;
  descriptor.value = async function (...args: any[]) {
    if (!isDevtoolEnabled()) {
      return originalMethod.apply(this, args);
    }

    const entity = this.constructor.name.slice(1, -13);
    const canceler$ = new Subject<void>();
    this.updater$.next({
      name: `[${entity}] ${originalMethod.name}`,
      type: "start",
      canceler$,
    });
    const result = originalMethod.apply(this, args);
    this.updater$.next({
      name: `[${entity}] ${originalMethod.name}`,
      type: "end",
    });
    canceler$.next();
    return result;
  };
  return descriptor;
}

function ReduxTrackObservable(
  target: any,
  propertyName: string,
  descriptor: PropertyDescriptor,
) {
  const originalMethod = descriptor.value;
  descriptor.value = function (...args: any[]) {
    if (!isDevtoolEnabled()) {
      return originalMethod.apply(this, args);
    }
    const entity = this.constructor.name.slice(1, -13);
    const canceler$ = new Subject<void>();
    this.updater$.next({
      name: `[${entity}] ${originalMethod.name}`,
      canceler$,
      type: "start",
    });
    return originalMethod.apply(this, args).pipe(
      tap(() => {
        this.updater$.next({
          name: `[${entity}] ${originalMethod.name}`,
          type: "end",
        });
        canceler$.next();
      }),
    );
  };
  return descriptor;
}

export function ReduxTrack(
  target: any,
  propertyName: string,
  descriptor: PropertyDescriptor,
) {
  const originalMethod = descriptor.value.toString();
  if (originalMethod.match(/^[^{]+{\s*return __async/m)) {
    return ReduxTrackAsync(target, propertyName, descriptor);
  } else if (originalMethod.match(/^\w+Observable\(/m)) {
    return ReduxTrackObservable(target, propertyName, descriptor);
  } else {
    return ReduxTrackSync(target, propertyName, descriptor);
  }
}
